import React, { useState } from "react";
import { useDebounce } from "usehooks-ts";

import { getInitialsFromFullName } from "../../constants/constants";
import { AddContactModal } from "../../features/contacts/modals/addContact/AddContactModal";
import { useAppSelector } from "../../redux/redux";
import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import { useGetSearchCustomersQuery } from "../../slices/ContactsSlice";
import { SelectOptionType } from "../../types/types";
import CustomSelect from "./Select";

type Props = {
  onChange: (value: SelectOptionType | null) => void;
  value: SelectOptionType | null;
  small?: boolean;
  contactKind?: "all" | "individuals" | "organizations";
};

export const SelectContacts: React.FC<Props> = ({
  onChange,
  value,
  small,
  contactKind = "all",
}) => {
  const [searchContact, setSearchContact] = useState("");
  const { user } = useAppSelector((state) => state.appReducer);

  const debounceSearchContact = useDebounce(searchContact, 50);

  const { data: contacts, isLoading: isLoadingContacts } =
    useGetSearchCustomersQuery({
      searchQuery: debounceSearchContact || "ab",
      orgId: getLinkedFirm()?.orgId || "",
      userId: user.id,
      isActive: true,
    });

  const [isOpenAddContact, setIsOpenAddContact] = useState(false);

  const getOptions = (): SelectOptionType[] => {
    const options: SelectOptionType[] = [];

    if (contacts) {
      const sortedContacts = [...contacts].sort((a, b) => {
        if (a.businessContactId && !b.businessContactId) {
          return 1;
        }
        if (!a.businessContactId && b.businessContactId) {
          return -1;
        }
        return 0;
      });
      sortedContacts
        .filter((contact) => {
          if (contactKind === "individuals") {
            return !contact.businessContactId;
          }

          if (contactKind === "organizations") {
            return !!contact.businessContactId;
          }

          return true;
        })
        .forEach((contact) => {
          const avatarColor: "purple" | "blue" = contact.businessContactId
            ? "purple"
            : "blue";
          options.push({
            value: contact?.customerId || "",
            label: `${contact.customerName}`,
            avatar: getInitialsFromFullName(contact?.customerName),
            avatarColor,
            contactId: contact.contactId || undefined,
            businessContactId: contact.businessContactId || undefined,
            customerId: contact.customerId || undefined,
          });
        });
    }

    return options;
  };

  return (
    <>
      <CustomSelect
        withAvatar={!!value?.value}
        options={getOptions()}
        onChange={(data) => {
          const newValue = data as SelectOptionType;
          if (newValue?.value === "create") {
            onChange(value);
            setIsOpenAddContact(true);
          } else {
            onChange(newValue);
          }
        }}
        setIsOpenAddContact={setIsOpenAddContact}
        placeholder={"Search contacts"}
        value={value}
        isLoading={isLoadingContacts}
        inputValue={searchContact}
        onChangeInput={setSearchContact}
        withMenu
        selectStyle={{
          ...(small ? { small: true } : { size: "md" }),
          fullWidth: true,
          listHeight: "200px",
          controlStyle: { backgroundColor: "#fff" },
        }}
      />
      {isOpenAddContact && (
        <AddContactModal
          isSearch
          isOpen={isOpenAddContact}
          closeModal={() => setIsOpenAddContact(false)}
          setContactsForSort={(newContact) => {
            setIsOpenAddContact(false);
            onChange({
              label: newContact?.name || "",
              value: newContact?.customerId || "",
              avatar: getInitialsFromFullName(newContact?.name || ""),
              avatarColor: newContact?.isBusinessContact ? "purple" : "blue",
            });
          }}
        />
      )}
    </>
  );
};
